import React, { useState } from 'react';
import { 
  MapPin, 
  MapLocationDot, 
  ChartPie, 
  PersonDigging, 
  CompassDrafting, 
  Shield, 
  CheckCircle2, 
  Users, 
  Building2, 
  Award, 
  Sparkles, 
  ArrowRight, 
  Lock, 
  FileCheck, 
  Search, 
  HelpCircle,
  Layers,
  Briefcase,
  ChevronRight
} from 'lucide-react';
import { Language, PageVariant } from '../../types';
import { TRANSLATIONS } from '../../translations';
import { IntakeModal } from './IntakeModal';
import { OpportunityCard, OpportunityListing } from './OpportunityCard';

interface Props {
  language?: Language;
  lang?: Language;
  setVariant?: (variant: PageVariant) => void;
  onNavigate?: (variant: PageVariant) => void;
  prefillCategory?: string;
}

export const ExploreOpportunitiesPage: React.FC<Props> = ({ 
  language: propLanguage, 
  lang, 
  setVariant, 
  onNavigate 
}) => {
  const activeLanguage = propLanguage || lang || Language.EN;
  const [isIntakeOpen, setIsIntakeOpen] = useState(false);
  const [selectedRef, setSelectedRef] = useState<string | undefined>(undefined);
  const [selectedCat, setSelectedCat] = useState<string | undefined>(undefined);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('All');
  const [selectedDeliveryMode, setSelectedDeliveryMode] = useState('All');

  const t = TRANSLATIONS[activeLanguage]?.exploreOpportunities || TRANSLATIONS[Language.EN].exploreOpportunities;

  // Empty state by default as per specification until approved live opportunities exist
  const liveListings: OpportunityListing[] = [];

  const handleNavigatePathway = (index: number) => {
    const variants = [
      PageVariant.EXPLORE_PROPERTY_SITES,
      PageVariant.EXPLORE_INVESTMENT_CAPITAL,
      PageVariant.EXPLORE_PROJECTS_DELIVERY,
      PageVariant.EXPLORE_PROFESSIONAL_EXPERTISE
    ];
    const target = variants[index];
    if (target) {
      if (setVariant) setVariant(target);
      else if (onNavigate) onNavigate(target);
      const urlMap = [
        'explore-property-sites',
        'explore-investment-capital',
        'explore-projects-delivery',
        'explore-professional-expertise'
      ];
      window.history.pushState({}, '', `?v=${urlMap[index]}`);
    }
  };

  const handleOpenIntake = (ref?: string, cat?: string) => {
    setSelectedRef(ref);
    setSelectedCat(cat);
    setIsIntakeOpen(true);
  };

  const scrollToOpportunities = () => {
    document.getElementById('current-opportunities')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Icons for pathways
  const pathwayIcons = [
    <i key="1" className="fa-solid fa-map-location-dot text-2xl" />,
    <i key="2" className="fa-solid fa-chart-pie text-2xl" />,
    <i key="3" className="fa-solid fa-person-digging text-2xl" />,
    <i key="4" className="fa-solid fa-compass-drafting text-2xl" />
  ];

  // Icons for Why Le Sector
  const whyIcons = [
    <Lock key="1" size={24} className="text-slate-900" />,
    <FileCheck key="2" size={24} className="text-slate-900" />,
    <Users key="3" size={24} className="text-slate-900" />,
    <Building2 key="4" size={24} className="text-slate-900" />,
    <Award key="5" size={24} className="text-slate-900" />,
    <Sparkles key="6" size={24} className="text-slate-900" />
  ];

  return (
    <div className="w-full bg-white text-slate-900 selection:bg-slate-900 selection:text-white pt-20">
      
      {/* 1. CINEMATIC HERO */}
      <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden bg-slate-950 text-white py-20">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=2070&auto=format&fit=crop" 
            alt="Architectural Estate Collaboration" 
            className="w-full h-full object-cover opacity-35 mix-blend-luminosity"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/70 to-slate-950/40"></div>
          {/* Grid pattern overlay */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] opacity-20"></div>
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col items-start pt-10">
          <div className="inline-flex items-center gap-2.5 px-3.5 py-1.5 bg-white/10 backdrop-blur-md border border-white/20 text-blue-300 text-[11px] uppercase tracking-[0.2em] font-bold mb-8">
            <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse"></span>
            {t.hero?.badge || 'LE SECTOR ECOSYSTEM • FACILITATED INTRODUCTIONS'}
          </div>

          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-heading uppercase tracking-widest text-white leading-[1.08] max-w-5xl mb-8">
            {t.hero?.headline1 || 'EVERY GREAT PROJECT STARTS WITH THE RIGHT CONNECTION.'}
          </h1>

          <p className="text-lg md:text-xl text-slate-300 font-light max-w-3xl leading-relaxed mb-12">
            {t.hero?.subheadline || 'Explore project opportunities and potential collaborations, professionally facilitated by Le Sector.'}
          </p>

          <div className="flex flex-col sm:flex-row gap-5 w-full sm:w-auto">
            <button 
              onClick={scrollToOpportunities}
              className="bg-white text-slate-950 px-8 py-4 uppercase tracking-widest text-xs font-black hover:bg-slate-200 transition-all flex items-center justify-center gap-3 shadow-xl hover:-translate-y-0.5"
            >
              {t.hero?.primaryCta || 'Explore Opportunities'} <ArrowRight size={16} />
            </button>
            <button 
              onClick={() => handleOpenIntake()}
              className="bg-slate-900/80 text-white border border-slate-700 backdrop-blur-md px-8 py-4 uppercase tracking-widest text-xs font-bold hover:bg-slate-800 transition-all flex items-center justify-center gap-3 hover:-translate-y-0.5"
            >
              {t.hero?.secondaryCta || "Tell Us What You're Looking For"}
            </button>
          </div>
        </div>
      </section>


      {/* 2. WHAT CAN YOU EXPLORE? (4 Core Pathways) */}
      <section className="py-24 px-6 md:px-12 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500 mb-3">
              {t.pathways?.tagline || 'Ecosystem Pillars'}
            </h2>
            <h3 className="text-3xl md:text-5xl font-heading uppercase tracking-widest text-slate-900 mb-6">
              {t.pathways?.title || 'What Can You Explore?'}
            </h3>
            <p className="text-base md:text-lg text-slate-600 leading-relaxed font-light">
              {t.pathways?.desc || 'Discover active pathways across residential real estate, development capital, and modular project delivery.'}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {(t.pathways?.items || []).map((item: any, i: number) => (
              <div 
                key={i} 
                onClick={() => handleNavigatePathway(i)}
                className="bg-white p-8 border border-slate-200 hover:border-slate-900 transition-all shadow-sm hover:shadow-lg flex flex-col justify-between group cursor-pointer"
              >
                <div>
                  <div className="w-14 h-14 bg-slate-900 text-white flex items-center justify-center mb-8 group-hover:scale-105 transition-transform group-hover:bg-blue-600">
                    {pathwayIcons[i]}
                  </div>
                  <h4 className="text-xl font-heading uppercase tracking-widest text-slate-900 mb-4 group-hover:text-blue-600 transition-colors">
                    {item.title}
                  </h4>
                  <p className="text-sm text-slate-600 leading-relaxed font-normal">
                    {item.desc}
                  </p>
                </div>
                <div className="mt-8 pt-4 border-t border-slate-100 flex items-center justify-between text-xs font-bold text-slate-900 uppercase tracking-wider group-hover:text-blue-600 transition-colors">
                  <span>{t.pathways?.exploreAction || 'Explore Pathway'}</span>
                  <ChevronRight size={16} className="transform group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* 3. HOW IT WORKS (5-Step Facilitated Journey) */}
      <section className="py-24 px-6 md:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500 mb-3">
              {t.howItWorks?.tagline || 'Facilitated Journey'}
            </h2>
            <h3 className="text-3xl md:text-5xl font-heading uppercase tracking-widest text-slate-900">
              {t.howItWorks?.title || 'How It Works'}
            </h3>
          </div>

          <div className="relative">
            {/* Desktop horizontal timeline bar */}
            <div className="hidden lg:block absolute top-12 left-0 right-0 h-px bg-slate-200 z-0"></div>

            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-8 relative z-10">
              {(t.howItWorks?.steps || []).map((step: any, i: number) => (
                <div key={i} className="flex flex-col items-start bg-white p-6 border border-slate-200 lg:border-none lg:p-0">
                  <div className="w-20 h-20 bg-slate-900 text-white border-2 border-slate-900 shadow-md flex items-center justify-center mb-6 relative">
                    <span className="text-xl font-heading tracking-widest">{step.step}</span>
                  </div>
                  <h4 className="text-lg font-heading uppercase tracking-widest text-slate-900 mb-3">
                    {step.title}
                  </h4>
                  <p className="text-xs text-slate-600 leading-relaxed">
                    {step.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>


      {/* 4. WHY THROUGH LE SECTOR? (Trust & Value Drivers) */}
      <section className="py-24 px-6 md:px-12 bg-slate-950 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-xs font-bold uppercase tracking-[0.25em] text-blue-400 mb-3">
              {t.why?.tagline || 'The Le Sector Advantage'}
            </h2>
            <h3 className="text-3xl md:text-5xl font-heading uppercase tracking-widest text-white mb-6">
              {t.why?.title || 'Why Through Le Sector?'}
            </h3>
            <p className="text-base text-slate-300 font-light leading-relaxed">
              {t.why?.desc || 'A professionally facilitated opportunity network free from unvetted forums, social media noise, and cold solicitations.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {(t.why?.features || []).map((feat: any, i: number) => (
              <div key={i} className="p-8 bg-slate-900 border border-slate-800 flex flex-col justify-between hover:border-slate-700 transition-colors">
                <div>
                  <div className="w-12 h-12 bg-white flex items-center justify-center mb-6">
                    {whyIcons[i]}
                  </div>
                  <h4 className="text-xl font-heading uppercase tracking-widest text-white mb-3">
                    {feat.title}
                  </h4>
                  <p className="text-sm text-slate-400 leading-relaxed">
                    {feat.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* 5. OPPORTUNITY SCENARIOS (Illustrative Use Cases) */}
      <section className="py-24 px-6 md:px-12 bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500 mb-3">
              {t.scenarios?.tagline || 'Illustrative Use Cases'}
            </h2>
            <h3 className="text-3xl md:text-5xl font-heading uppercase tracking-widest text-slate-900 mb-4">
              {t.scenarios?.title || 'Opportunity Scenarios'}
            </h3>
            <p className="text-xs text-slate-500 max-w-2xl mx-auto italic">
              {t.scenarios?.desc || 'Real-world examples showing how stakeholders connect within our ecosystem. (Illustrative scenarios — distinct from live directory listings).'}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {(t.scenarios?.items || []).map((sc: any, i: number) => (
              <div 
                key={i} 
                className="bg-white p-8 border-2 border-slate-200 relative flex flex-col justify-between shadow-sm"
              >
                <div>
                  <div className="inline-block px-2.5 py-1 bg-slate-100 border border-slate-300 text-[9px] font-bold uppercase tracking-widest text-slate-600 mb-6">
                    {t.scenarios?.badge || 'USE CASE SCENARIO'}
                  </div>

                  <h4 className="text-2xl font-heading uppercase tracking-widest text-slate-900 mb-4">
                    {sc.title}
                  </h4>

                  <blockquote className="text-xs italic text-slate-700 leading-relaxed p-4 bg-slate-50 border-l-2 border-slate-900 mb-6">
                    {sc.quote}
                  </blockquote>
                </div>

                <div className="pt-4 border-t border-slate-100">
                  <span className="text-[10px] uppercase tracking-wider text-slate-400 block mb-1 font-bold">
                    {t.scenarios?.targetMatchLabel || 'Target Match:'}
                  </span>
                  <span className="text-xs font-bold text-slate-800">
                    {sc.targetMatch}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* 6. CURRENT OPPORTUNITIES DIRECTORY & EMPTY STATE ENGINE */}
      <section id="current-opportunities" className="py-24 px-6 md:px-12 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
            <div>
              <h2 className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500 mb-3">
                {t.directory?.tagline || 'Active Directory'}
              </h2>
              <h3 className="text-3xl md:text-5xl font-heading uppercase tracking-widest text-slate-900">
                {t.directory?.title || 'Current Opportunities'}
              </h3>
            </div>

            {/* Filter Controls */}
            <div className="flex flex-wrap gap-3">
              <select 
                value={selectedCategoryFilter}
                onChange={(e) => setSelectedCategoryFilter(e.target.value)}
                className="bg-slate-50 border border-slate-200 text-xs text-slate-800 px-4 py-2.5 font-bold uppercase tracking-wider focus:outline-none focus:border-slate-900"
              >
                <option value="All">{t.directory?.filterAllCategories || 'All Categories'}</option>
                <option value="Property">{t.pathways?.items?.[0]?.title || 'Property & Sites'}</option>
                <option value="Investment">{t.pathways?.items?.[1]?.title || 'Investment & Capital'}</option>
                <option value="Projects">{t.pathways?.items?.[2]?.title || 'Projects & Delivery'}</option>
                <option value="Expertise">{t.pathways?.items?.[3]?.title || 'Professional Expertise'}</option>
              </select>

              <select 
                value={selectedDeliveryMode}
                onChange={(e) => setSelectedDeliveryMode(e.target.value)}
                className="bg-slate-50 border border-slate-200 text-xs text-slate-800 px-4 py-2.5 font-bold uppercase tracking-wider focus:outline-none focus:border-slate-900"
              >
                <option value="All">{t.directory?.filterAllModes || 'All Delivery Modes'}</option>
                <option value="Traditional">{t.directory?.modeTraditional || 'Traditional Custom'}</option>
                <option value="Modular">{t.directory?.modeModular || 'Modular Assembly'}</option>
                <option value="Hybrid">{t.directory?.modeHybrid || 'Hybrid Delivery'}</option>
              </select>
            </div>
          </div>

          {/* Directory Grid OR Empty State */}
          {liveListings.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {liveListings.map((listing) => (
                <OpportunityCard
                  key={listing.id}
                  listing={listing}
                  language={activeLanguage}
                  onExpressInterest={(refCode, category) => handleOpenIntake(refCode, category)}
                />
              ))}
            </div>
          ) : (
            /* Managed Professional Empty State Engine */
            <div className="bg-slate-50 border border-slate-200 p-12 md:p-16 text-center max-w-3xl mx-auto shadow-sm">
              <div className="w-16 h-16 bg-slate-900 text-white flex items-center justify-center mx-auto mb-6">
                <Search size={28} />
              </div>

              <h4 className="text-2xl font-heading uppercase tracking-widest text-slate-900 mb-4">
                {t.directory?.emptyState?.title || 'New opportunities are currently being reviewed.'}
              </h4>

              <p className="text-sm text-slate-600 leading-relaxed max-w-xl mx-auto mb-8 font-normal">
                {t.directory?.emptyState?.desc || "Tell us what you're looking for and we'll help determine whether there may be a suitable opportunity within the Le Sector network."}
              </p>

              <button
                onClick={() => handleOpenIntake()}
                className="bg-slate-900 text-white px-8 py-4 uppercase tracking-widest text-xs font-bold hover:bg-slate-800 transition-colors inline-flex items-center gap-3"
              >
                {t.directory?.emptyState?.cta || "Tell Us What You're Looking For"} <ArrowRight size={16} />
              </button>
            </div>
          )}
        </div>
      </section>


      {/* 7. PRIVACY, DISCRETION & TRUST GUARANTEE */}
      <section className="py-20 px-6 md:px-12 bg-slate-100 border-t border-slate-200">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-slate-200 text-slate-700 text-[10px] font-bold uppercase tracking-widest mb-3">
              <Shield size={12} className="text-slate-900" />
              {t.privacy?.tagline || 'Discretion & Security'}
            </div>
            <h3 className="text-3xl font-heading uppercase tracking-widest text-slate-900 mb-3">
              {t.privacy?.title || 'Privacy, Discretion & Trust Guarantee'}
            </h3>
            <p className="text-sm text-slate-600 max-w-2xl mx-auto">
              {t.privacy?.desc || 'We safeguard your identity, project details, and commercial interests at every step.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {(t.privacy?.items || []).map((p: any, i: number) => (
              <div key={i} className="bg-white p-6 border border-slate-200 shadow-sm">
                <div className="w-10 h-10 bg-slate-900 text-white flex items-center justify-center mb-4">
                  <CheckCircle2 size={20} />
                </div>
                <h4 className="text-base font-bold uppercase tracking-widest text-slate-900 mb-2">
                  {p.title}
                </h4>
                <p className="text-xs text-slate-600 leading-relaxed">
                  {p.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* 8. FINAL CALL TO ACTION */}
      <section className="relative py-28 px-6 md:px-12 bg-slate-950 text-white overflow-hidden">
        <div className="max-w-4xl mx-auto text-center relative z-10 border border-slate-800 p-10 md:p-16 bg-slate-900/90 shadow-2xl">
          <h2 className="text-3xl md:text-5xl font-heading uppercase tracking-widest mb-6 text-white">
            {t.cta?.title || 'Have an Opportunity in Mind?'}
          </h2>
          <p className="text-base md:text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed mb-10 font-light">
            {t.cta?.desc || "Tell us what you're looking for and let Le Sector help determine whether there may be a suitable connection within our network."}
          </p>
          <button
            onClick={() => handleOpenIntake()}
            className="bg-white text-slate-950 px-10 py-5 uppercase tracking-widest text-xs font-black hover:bg-slate-200 transition-all shadow-xl hover:-translate-y-0.5 inline-flex items-center gap-3"
          >
            {t.cta?.button || "Tell Us What You're Looking For"} <ArrowRight size={16} />
          </button>
        </div>
      </section>


      {/* 9. CONFIDENTIAL INTAKE MODAL */}
      <IntakeModal
        isOpen={isIntakeOpen}
        onClose={() => setIsIntakeOpen(false)}
        language={activeLanguage}
        prefillOpportunityRef={selectedRef}
        prefillCategory={selectedCat}
      />

    </div>
  );
};
